import React, { useEffect, useState } from "react";
import { PulseLoader, PulseTextField } from "@pulselabs-ai/shared-fe-ui";
import MarkdownRenderer from "../../MarkdownRenderer/MarkdownRenderer";
import MfaSDKCodeService, {
  SDKCodeResponse,
} from "../../../services/MfaSDKCodeService";
import { formatSDKInstructionsV2 } from "../../../utils/sdkInstructionsFormatter";
import { useSelector } from "react-redux";
import { selectSelectedProjectId } from "../../../store/slices/onboardingSlice";
import { selectUser } from "../../../store/slices/userSlice";

interface Step5InstallationProps {
  productType: string;
  programmingLanguage: string;
  usesScenes: string;
  uiEntryPoint: string;
  rootWidgetType: string;
  os: string;
}

const Step5Installation: React.FC<Step5InstallationProps> = ({
  productType,
  programmingLanguage,
  usesScenes,
  uiEntryPoint,
  rootWidgetType,
  os,
}) => {
  const [markdownContent, setMarkdownContent] = useState("");
  const [instruction, setInstruction] = useState<SDKCodeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const projectId = useSelector(selectSelectedProjectId) || "";
  const user = useSelector(selectUser);

  useEffect(() => {
    const fetchInstructions = async () => {
      try {
        const sdkService = MfaSDKCodeService.getInstance();

        // Determine parameters based on productType
        let parameters = {};

        if (productType === "web") {
          // For web platform (empty parameters)
          parameters = {};
        } else {
          // For mobile platforms

          // They don't need to specify the programming language for flutter and reactnative
          if (productType === "flutter") {
            programmingLanguage = "dart";
          } else if (productType === "reactnative") {
            programmingLanguage = "typescript";
          }

          parameters = {
            ...(usesScenes && { UseScenes: usesScenes }),
            ...(uiEntryPoint && { UIEntryPoint: uiEntryPoint }),
            ...(rootWidgetType && { RootWidgetType: rootWidgetType }),
            ...(os && { deviceType: os }),
          };
        }

        console.log("Fetching SDK instructions for:", {
          platform: productType,
          codingLanguage: programmingLanguage,
          parameters: parameters,
        });

        const response = await sdkService.getSDKCodeByParameters(
          user?.token || "",
          {
            platform: productType,
            codingLanguage: programmingLanguage,
            projectId: projectId,
            parameters: JSON.stringify(parameters),
          },
        );
        setInstruction(response);
        setMarkdownContent(
          formatSDKInstructionsV2(response, programmingLanguage),
        );
      } catch (err) {
        setError("Failed to load installation instructions");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInstructions();
  }, [
    productType,
    programmingLanguage,
    usesScenes,
    uiEntryPoint,
    rootWidgetType,
    os,
    projectId,
    user?.token,
  ]);

  if (loading)
    return (
      <div>
        <PulseLoader />
      </div>
    );
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <MarkdownRenderer
        content={markdownContent}
        className="installation-guide"
      />
      <div className="api-key-section">
        <h2 className="pl-heading-xs-extra-bold">API Key</h2>
        <PulseTextField value={instruction?.appKeys?.[0]?.appKey || ""} />
      </div>
    </>
  );
};

export default Step5Installation;
